import {
  Button,
  capitalize,
  Chip,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ReactJson from 'react-json-view';
import AQDataGrid from '../../AQDataGrid';
import {
  dateFormatString,
  jobIconStatusMap,
  jobStatusColorMap,
} from '../utils';
import { formatInTimeZone } from 'date-fns-tz';
import { formatDistanceStrict, parseJSON } from 'date-fns';
import { useGlobalSettings } from '../../../app/GlobalSettingProvider';
import { useEntities, useEntity } from '../../../data/flowEntities';
import { v4 as uuidv4 } from 'uuid';
import CopyToClipboard from '../../CopyToClipboard';
import { IconLogs } from '@tabler/icons-react';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import JobLogsTable from './JobLogsTable';

const SyncChildJobsTable = ({
  currentAccount,
  syncId,
  refetchInterval,
  sync,
}: {
  currentAccount: any;
  syncId: string;
  refetchInterval: number;
  sync: any;
}) => {
  const { palette } = useTheme();
  const { settings } = useGlobalSettings();

  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: [
        'failed',
        'success',
        'running',
        'queued',
        'dequeued',
        'retry',
        'pushed',
      ],
      sortable: false,
      width: 110,
      renderCell: (params: { value: string; row: any }) => (
        <span
          style={{
            color: (jobStatusColorMap as Record<string, string>)[params.value],
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {
            (jobIconStatusMap as Record<string, React.ReactNode>)[
              params.value === 'pushed' ? 'queued' : params.value
            ]
          }{' '}
          <small style={{ paddingLeft: '5px' }}>
            {params.value === 'retry'
              ? `Retrying ${params.row.attempts}/${params.row.max_attempts}`
              : params.value === 'pushed'
                ? 'Queued'
                : capitalize(params.value)}
          </small>
        </span>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 120,
      sortable: false,
      renderCell: (params: any) =>
        params.value.split('-')[params.value.split('-').length - 1],
    },
    {
      field: 'job_type',
      headerName: 'Type',
      width: 150,
      sortable: false,
      renderCell: (params: any) => {
        let jobType = params.row?.job_type || params.row?.apiType;
        if (jobType === 'data-batch' || jobType === 'snapshot') {
          jobType = 'load: ' + jobType;
        }
        if (!jobType) return '';
        return capitalize(jobType);
      },
    },
    {
      field: 'relative_path',
      headerName: 'Object',
      width: 100,
      sortable: false,
      renderCell: (params: any) => {
        return params.row?.relative_path?.split('/')?.pop() || '';
      },
    },
    {
      field: 'run_at',
      headerName: 'Queued Time',
      width: 220,
      sortable: true,
      filterable: false,
      renderCell: (params: { value?: string }) => (
        <Tooltip title={(params?.value ?? '') + ' UTC'}>
          <span>
            {params?.value
              ? formatInTimeZone(
                  parseJSON(params?.value),
                  settings.timeZone.current,
                  dateFormatString,
                )
              : ''}
          </span>
        </Tooltip>
      ),
    },

    {
      field: 'started_at',
      headerName: 'Started',
      width: 220,
      sortable: true,
      filterable: false,
      renderCell: (params: { value?: string }) => (
        <Tooltip title={(params?.value ?? '') + ' UTC'}>
          <span>
            {params?.value
              ? formatInTimeZone(
                  parseJSON(params?.value),
                  settings.timeZone.current,
                  dateFormatString,
                )
              : ''}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'ended_at',
      headerName: 'Ended',
      width: 220,
      sortable: true,
      filterable: false,
      renderCell: (params: { value?: string }) => (
        <Tooltip title={(params?.value ?? '') + ' UTC'}>
          <span>
            {params?.value
              ? formatInTimeZone(
                  parseJSON(params?.value),
                  settings.timeZone.current,
                  dateFormatString,
                )
              : ''}
          </span>
        </Tooltip>
      ),
    },
    // add a hover that gives the distance between started and ended in a number
    {
      field: 'run_time',
      headerName: 'Run time',
      filterable: false,
      sortable: false, //TODO: maybe sort by relative time asc/desc in ms
      width: 'auto',
      flex: 1,
      valueGetter: (
        _value: any,
        row: { ended_at?: string; started_at?: string },
      ) =>
        row?.ended_at &&
        row?.started_at &&
        formatDistanceStrict(
          parseJSON(row.ended_at),
          parseJSON(row.started_at),
        ),
    },
    {
      headerName: 'Job Logs',
      width: 'auto',
      flex: 1,
      field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
      renderCell: (params: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#0288d1',
            }}
          >
            <IconLogs size={16} />
            <span style={{ marginLeft: '5px' }}>
              {params.isDetailPanelExpanded ? 'Hide Logs' : 'View Logs'}
            </span>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <AQDataGrid
        id="-child-jobs-table"
        dataQueryHook={useEntities}
        buildQueryArgs={(queryParams: any) => [
          currentAccount?.id,
          `jobs/${syncId}/child-jobs`,
          { useExplicitEntityType: true, ...queryParams },
          { refetchInterval },
        ]}
        sx={{
          container: {
            maxWidth: '100%',
            width: '100%',
          },
        }}
        colDefs={columns}
        gridProps={{
          getDetailPanelHeight: () => 'auto',
          pinnedRows: {
            topFilter: (row: any) => row?.job_type === 'extract',
          },
          getRowClassName: (params: { row: any }) =>
            `aq-datagrid-row--${params.row.status}`,
        }}
      />
    </>
  );
};

export default SyncChildJobsTable;
